<template>
  <div class="h-100">
    <app-home />

    <!-- Dialog for general messages -->
    <custom-dialog
      v-model="dialog.show"
      :title="dialog.title"
      :text="dialog.text"
      :action="dialog.action"
    />
    <claim-profile-dialog v-model="claimProfileDialog" />

    <!-- Websocket disconnection dialog -->
    <connection-lost-dialog
      v-model="wsDisconnected"
      :reconnecting="wsReconnecting"
      :attempts="wsReconnectAttempts"
      @reconnect="attemptReconnect"
    />

    <!-- Terms of Service Dialog -->
    <to-s-dialog
      :show="showTosDialog"
      :content="ToSContent"
      :is-update="isTosReacceptance"
      :current-version="$store.state.auth.currentTosVersion || ''"
      @accept="acceptTos"
      @decline="declineTos"
    />

    <!-- Subscription Dialog -->
    <v-dialog
      v-model="subscriptionDialog.show"
      max-width="1040"
      width="96vw"
      v-if="!isShareLink() && isBillingEnabled"
    >
      <v-card class="billing-shell-card" flat>
        <PurchaseSubscription
          :inquiry-only="subscriptionDialog.inquiryOnly"
          :open-enterprise-token="subscriptionDialog.openEnterpriseToken"
          :preset-deployment-type="subscriptionDialog.enterprisePresetDeployment"
          @enterprise-modal-visibility="onEnterpriseModalVisibility"
        />
      </v-card>
    </v-dialog>

    <!-- Google One Tap prompt on login and for demo sessions -->
    <google-one-tap
      v-if="showGoogleOneTap"
      :key="googleOneTapKey"
      :reprompt-ms="60000"
    />
  </div>
</template>

<script>
import CustomDialog from "@/components/common/Dialog.vue";
import ConnectionLostDialog from "@/components/common/ConnectionLostDialog.vue";
import ToSDialog from "@/components/common/ToSDialog.vue";
import ClaimProfileDialog from "@/components/common/ClaimProfileDialog.vue";
import { createRgbVarsForThemes } from "@/js/utils.vuetify.css";
import { getPredictors, getUserSettings } from "@/js/api.schema";
import { isTosReacceptance, requiresTosAcceptance } from "@/js/utils.tos";
import { applyStudySummaryUserSettings } from "@/study-summary/settings";

import * as wsSingle from "@/js/api.websocket.single";
import {
  disconnect as disconnectSriesDownload,
  openSeriesWebsocket as openDownloadWs
} from "@/js/download/seriesDownload";
import AppHome from "@/views/Home";
import { mapGetters } from "vuex";
import Vue from "vue";
import PurchaseSubscription from "@/components/PurchaseSubscription.vue";
import GoogleOneTap from "@/plugins/auth/google/components/GoogleOneTap.vue";
import ToSString from "./ToS.js"; // if in same directory

export default {
  name: "App",
  components: {
    AppHome,
    ConnectionLostDialog,
    CustomDialog,
    GoogleOneTap,
    ClaimProfileDialog,
    ToSDialog,
    PurchaseSubscription
  },
  data() {
    return {
      dialog: { show: false, title: "", text: "", action: null },
      wsDisconnected: false,
      wsReconnecting: false,
      wsReconnectAttempts: 0,
      subscriptionDialog: {
        show: false,
        openEnterpriseToken: 0,
        inquiryOnly: false,
        enterprisePresetDeployment: ""
      },
      subscriptionCheckInterval: null,
      claimProfileDialog: false
    };
  },
  created() {
    // Connect to websocket, disconnect on page unload

    try {
      wsSingle.disconnect();
      disconnectSriesDownload();
    } catch (error) {
      // Handle the error here, you can log it or perform other actions.
      console.error("An error occurred:", error);
    }
  },
  mounted() {
    this.$root.$on("show-connection-dialog", () => {
      this.wsDisconnected = true;
    });
    this.$root.$on("open-claim-profile", this.openClaimProfileDialog);
    this.$root.$on("open-pricing-modal", this.openPricingModal);
    this.$root.$on("open-enterprise-inquiry", this.openEnterpriseInquiryModal);

    createRgbVarsForThemes(this.$vuetify.theme.themes.light);

    // Slight delay to ensure all components are ready
    setTimeout(() => {
      if (this.isAuth) {
        this.$store.dispatch("billing/refreshBillingData");
      }

      // Set 5-minute interval
      this.subscriptionCheckInterval = setInterval(() => {
        if (this.isAuth) {
          this.$store.dispatch("billing/refreshBillingData");
        }
      }, 5 * 60 * 1000); // 5 minutes
    }, 500); // Adjust delay if needed
  },
  beforeDestroy() {
    if (this.subscriptionCheckInterval) {
      clearInterval(this.subscriptionCheckInterval);
      this.subscriptionCheckInterval = null;
    }
    this.$root.$off("open-claim-profile", this.openClaimProfileDialog);
    this.$root.$off("open-pricing-modal", this.openPricingModal);
    this.$root.$off("open-enterprise-inquiry", this.openEnterpriseInquiryModal);
  },
  computed: {
    ...mapGetters("auth", ["isAuth"]),
    ...mapGetters("billing", ["isBillingEnabled"]),
    requiresTosAcceptance() {
      return requiresTosAcceptance({
        user: this.$store.state.auth.user,
        tosUpdateRequired: this.$store.state.auth.tosUpdateRequired
      });
    },
    isTosReacceptance() {
      return isTosReacceptance({
        user: this.$store.state.auth.user,
        tosUpdateRequired: this.$store.state.auth.tosUpdateRequired
      });
    },
    showTosDialog() {
      const user = this.$store.state.auth.user;
      return (
        this.isAuth &&
        !this.isShareLink() &&
        Boolean(user) &&
        (this.requiresTosAcceptance || !user.accepted_tos)
      );
    },
    ToSContent() {
      return ToSString; // Ensure Vue tracks it
    },
    hasPaidSubscription() {
      return this.$store.state.auth.user?.subscription_tier !== "free";
    },
    showGoogleOneTap() {
      const isDemoUser = this.$store.state.auth.user?.is_demo === true;
      const isLoginRoute = this.$route?.name === "login";
      return isDemoUser || isLoginRoute;
    },
    googleOneTapKey() {
      const routeName = this.$route?.name || "no-route";
      const userId = this.$store.state.auth.user?.id || "anon";
      const isDemoUser = this.$store.state.auth.user?.is_demo ? "demo" : "std";
      return `${routeName}:${userId}:${isDemoUser}`;
    }
  },
  methods: {
    openClaimProfileDialog() {
      this.claimProfileDialog = true;
    },
    openPricingModal() {
      if (!this.isBillingEnabled || this.isShareLink()) {
        return;
      }
      this.subscriptionDialog.inquiryOnly = false;
      this.subscriptionDialog.enterprisePresetDeployment = "";
      this.subscriptionDialog.show = true;
    },
    openEnterpriseInquiryModal() {
      if (!this.isBillingEnabled) {
        window.location.href = "mailto:info@dicom.vision";
        return;
      }
      this.subscriptionDialog.inquiryOnly = true;
      this.subscriptionDialog.enterprisePresetDeployment = "MDR";
      this.subscriptionDialog.show = true;
      this.subscriptionDialog.openEnterpriseToken += 1;
    },
    onEnterpriseModalVisibility(isVisible) {
      if (this.subscriptionDialog.inquiryOnly && isVisible) {
        this.subscriptionDialog.show = false;
      }
      if (!isVisible) {
        this.subscriptionDialog.inquiryOnly = false;
        this.subscriptionDialog.enterprisePresetDeployment = "";
      }
    },
    isShareLink() {
      return !!this.$route.params.shareId;
    },
    isDownloadEnabled() {
      return true; //return !this.isAuth;
    },
    isUploadEnabled() {
      return true; // return this.isAuth && !this.$isDemoUser;
    },
    connectWebsocket() {
      this.wsDisconnected = false;
      this.wsReconnecting = false;

      const onClose = () => {
        if (this.isShareLink()) {
          return;
        }
        if (!this.wsDisconnected) {
          this.wsDisconnected = true;
        }
      };
      const onError = () => {
        if (this.isShareLink()) {
          return;
        }
        if (!this.wsDisconnected) {
          this.wsDisconnected = true;
        }
      };

      const userId =
        (this.$store.state.auth.user && this.$store.state.auth.user.uuid) ||
        this.user;
      if (this.isDownloadEnabled())
        openDownloadWs(userId, onClose, onError, this.$router);
    },
    attemptReconnect() {
      this.wsReconnecting = true;
      this.wsReconnectAttempts++;

      // Disconnect existing sockets before reconnecting
      try {
        wsSingle.disconnect();
        disconnectSriesDownload();
      } catch (e) {
        // ignore cleanup errors
      }

      // Small delay to let the old connections close cleanly
      setTimeout(() => {
        this.wsDisconnected = false;
        this.wsReconnecting = false;
        this.connectWebsocket();
      }, 1000);
    },
    async loadPredictors() {
      // get all predictors
      const predictors = await getPredictors();
      this.$store.commit("viewer/initPredictors", predictors);
    },
    loadUserSettings() {
      getUserSettings()
        .then(data => {
          // update store values
          this.$store.commit("viewer/initOptions", data);
          if (data.show_ground_truth_panel !== undefined) {
            this.$store.commit(
              "viewer/setShowGroundTruthPanel",
              data.show_ground_truth_panel
            );
          }

          // update dashboard series display settings
          if (data.hide_for_processing_series !== undefined) {
            this.$store.commit(
              "dashboard/setHideForProcessingSeries",
              data.hide_for_processing_series
            );
          }
          if (data.hide_non_imaging_series !== undefined) {
            this.$store.commit(
              "dashboard/setHideNonImagingSeries",
              data.hide_non_imaging_series
            );
          }
          if (data.group_proj_raw_series !== undefined) {
            this.$store.commit(
              "dashboard/setGroupProjRawSeries",
              data.group_proj_raw_series
            );
          }
          if (data.dashboard_triage_colors_enabled !== undefined) {
            this.$store.commit(
              "dashboard/setTriageColorsEnabled",
              data.dashboard_triage_colors_enabled
            );
          }
          applyStudySummaryUserSettings(this.$store, data);
          if (data.dark_mode !== undefined) {
            this.$store.commit("setDarkMode", data.dark_mode);
          }
          if (data.compact_navbar !== undefined) {
            this.$store.commit("setCompactNavbar", data.compact_navbar);
          }
          if (data.dashboard_group !== undefined) {
            this.$store.commit("dashboard/updateGroup", data.dashboard_group);
          }
          const rawPrefs = data.dashboard_column_prefs;
          let parsedPrefs = rawPrefs;
          if (typeof rawPrefs === "string") {
            try {
              parsedPrefs = JSON.parse(rawPrefs);
            } catch (err) {
              parsedPrefs = {};
              if (process.env.NODE_ENV !== "production") {
                console.warn("Invalid dashboard_column_prefs JSON", err);
              }
            }
          }
          if (parsedPrefs && typeof parsedPrefs === "object") {
            this.$store.commit("dashboard/SET_COLUMN_PREFS", parsedPrefs);
          }
        })
        .catch(error => console.error(error));
    },
    async acceptTos() {
      try {
        const locale = (navigator.language || "en").slice(0, 10);
        const versionFromStore =
          this.$store.state.auth.currentTosVersion ||
          this.$store.state.auth.user?.tos_version ||
          "legacy";
        const response = await Vue.$http.patch("auth/users/accept-tos", null, {
          accepted_tos: true,
          tos_version: versionFromStore,
          locale,
          source: "web_dialog"
        });
        const payload = response.body || response.data || {};
        const acceptedVersion = payload.current_tos_version || versionFromStore;

        // Update the user object in Vuex
        this.$store.commit("auth/updateUser", {
          accepted_tos: true,
          tos_version: acceptedVersion,
          tos_locale: locale,
          tos_accept_source: "web_dialog",
          tos_accepted_at: new Date().toISOString()
        });
        this.$store.commit("auth/update", {
          key: "tosUpdateRequired",
          value: false
        });
        this.$store.commit("auth/update", {
          key: "currentTosVersion",
          value: acceptedVersion
        });
      } catch (error) {
        const payload = error.body || error.data || {};
        if (payload.current_tos_version) {
          this.$store.commit("auth/update", {
            key: "currentTosVersion",
            value: payload.current_tos_version
          });
        }
        console.error("Failed to update TOS acceptance status:", error);
      }
    },
    declineTos() {
      // Handle TOS decline (e.g., log out the user or show a warning)
      this.$store.dispatch("auth/logout");
      this.$router.push("/login");
    }
  },

  watch: {
    $route() {
      if (this.isShareLink()) {
        this.wsDisconnected = false;
        this.wsReconnecting = false;
      }
    },
    showGoogleOneTap(show) {
      // When the One Tap condition becomes false (non-demo user logged in),
      // explicitly cancel any lingering browser-level Google prompt.
      if (!show && window.google && window.google.accounts) {
        window.google.accounts.id.cancel();
      }
    },
    isAuth(value) {
      this.$store.commit("viewer/initViewerSessionId");
      if (value) {
        this.loadPredictors();
        this.loadUserSettings();
        this.$store.dispatch("billing/refreshBillingData");
        this.connectWebsocket();

      } else {
        this.$store.commit("billing/reset");
        this.subscriptionDialog.show = false;
      }
    },
    isBillingEnabled(value) {
      if (!value) {
        this.subscriptionDialog.show = false;
      }
    }
  }
};
</script>

<style lang="scss">
@import "@/styles/utils.scss";
@import "@/styles/fonts.scss";
@import "@/styles/main.scss";

.billing-shell-card {
  background: #1e1e1e !important;
  border: 1px solid #3a3a3a;
  color: #f5f5f5 !important;
  border-radius: 16px !important;
  overflow: hidden;
}
</style>
